import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getOrgDashboard } from '../lib/api';

/**
 * /o/:orgSlug/ai-systems — Cross-workspace AI systems inventory.
 *
 * Placeholder page showing per-workspace system counts. When the
 * org-tier AI-systems API ships this will pull a consolidated
 * inventory; for now it reuses the org dashboard's workspace list
 * to show where systems are registered.
 */

export function OrgAiSystems() {
  const { orgSlug } = useParams<{ orgSlug: string }>();

  const dashboardQuery = useQuery({
    queryKey: queryKeys.orgAiSystems(orgSlug ?? ''),
    queryFn: () => getOrgDashboard(orgSlug!),
    enabled: !!orgSlug,
  });
  const dashboard = {
    data: dashboardQuery.data ?? null,
    error: dashboardQuery.error?.message ?? null,
    loading: dashboardQuery.isLoading,
  };

  if (dashboard.loading) return <Spinner label="Loading AI systems inventory" />;
  if (dashboard.error) return <Alert variant="error">{dashboard.error}</Alert>;
  if (!dashboard.data) return null;

  const workspaces = dashboard.data.workspaces;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="AI systems"
        subtitle="Cross-workspace inventory of registered AI systems."
      />

      {workspaces.length === 0 ? (
        <EmptyState
          title="No workspaces"
          description="AI systems will appear here once workspaces register use cases and discovered tools."
        />
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {workspaces.map((ws) => (
            <li key={ws.workspace_id}>
              <Link
                to={`/w/${ws.slug}/app/use-cases`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  textDecoration: 'none',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{ws.name}</span>
                  <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                    {ws.item_count} register item{ws.item_count === 1 ? '' : 's'}
                  </span>
                </div>
                <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                  {ws.score}%
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}

      <div
        style={{
          border: '1px dashed var(--border-strong)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-5)',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        A consolidated cross-workspace AI systems inventory with risk classification,
        lifecycle status, and compliance mapping is planned for a future release.
      </div>
    </div>
  );
}
